import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home as HomeIcon,
  Users,
  FileText,
  Heart,
  ArrowRight,
  ShieldCheck,
  HeartHandshake,
  Briefcase,
  Compass,
  Sparkles,
  Target,
  UserCheck,
  GraduationCap,
  Network,
} from 'lucide-react';
import './Home.css';

const Home = () => {
  const whoWeServe = [
    { icon: Users, text: 'Men in recovery' },
    { icon: HeartHandshake, text: 'Women in recovery' },
    { icon: UserCheck, text: 'Individuals working toward family reunification' },
    { icon: Briefcase, text: 'Individuals seeking employment and independent living' },
    { icon: Compass, text: 'Individuals seeking stability, accountability, and community support' },
  ];

  const whyChoose = [
    { icon: Sparkles, label: 'Recovery-Focused Environment' },
    { icon: HeartHandshake, label: "Women's Recovery Program" },
    { icon: Users, label: "Men's Recovery Program" },
    { icon: Briefcase, label: 'Employment & Workforce Readiness' },
    { icon: GraduationCap, label: 'Life Skills Development' },
    { icon: Compass, label: 'Community Resource Navigation' },
    { icon: Network, label: 'Family Reconnection Support' },
    { icon: Target, label: 'Individualized Goal Planning' },
    { icon: ShieldCheck, label: 'Safe, Structured Housing' },
  ];

  const communityImpact = [
    'Maintain recovery',
    'Secure employment',
    'Build independent living skills',
    'Reconnect with family',
    'Access community resources',
    'Develop long-term stability',
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              ProSolve Global Solutions
              <span className="hero-subtitle-block">Recovery Houses</span>
            </h1>
            <p className="hero-description">
              Safe, structured housing and recovery support services for men and women rebuilding their lives.
            </p>
            <p className="hero-text">
              Our Pennsylvania Department of Drug and Alcohol Programs (DDAP) licensed program serves both men and women through recovery housing, recovery support services, life skills development, employment readiness, resource navigation, and community connections.
            </p>
            <div className="hero-cta">
              <Link to="/apply" className="btn btn-primary" data-testid="hero-apply-button">
                Apply Now
                <ArrowRight size={20} />
              </Link>
              <Link to="/contact" className="btn btn-secondary" data-testid="hero-contact-button">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">About Us</h2>
            <div className="title-underline"></div>
          </div>
          <div className="about-content">
            <p className="about-text">
              Recovery is easier when people have a safe place to live and a community that supports their progress.
            </p>
            <p className="about-text">
              ProSolve Global Solutions provides sober living homes and recovery support services in the Lehigh Valley where individuals can focus on continuing their recovery while rebuilding their daily lives. Our programs are designed to offer stability, accountability, and a supportive environment where residents develop healthy routines, reconnect with work and community, and move forward with confidence.
            </p>
            <p className="about-text">
              We operate two Pennsylvania Department of Drug and Alcohol Programs (DDAP) licensed recovery residences serving both men and women.
            </p>
          </div>
        </div>
      </section>

      {/* Our Programs Section (Women's featured first) */}
      <section className="houses-section" data-testid="our-programs-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">Our Programs</h2>
            <div className="title-underline"></div>
          </div>
          <div className="houses-grid">
            {/* Women's Program — featured first */}
            <div className="house-card" data-testid="program-card-womens">
              <div className="house-icon-wrapper">
                <HeartHandshake size={40} className="house-icon" />
              </div>
              <h3 className="house-title">Women&apos;s Recovery Program</h3>
              <p className="house-location">Catasauqua, PA</p>
              <p className="house-description">
                Supporting women in recovery through structured housing, accountability, recovery engagement, employment support, life skills development, and community connections.
              </p>
              <Link to="/womens-recovery" className="house-link">
                Learn More
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="house-card" data-testid="program-card-mens">
              <div className="house-icon-wrapper">
                <HomeIcon size={40} className="house-icon" />
              </div>
              <h3 className="house-title">Men&apos;s Recovery Program</h3>
              <p className="house-location">Whitehall, PA</p>
              <p className="house-description">
                Supporting men in recovery through structured housing, accountability, recovery engagement, employment support, life skills development, and community connections.
              </p>
              <Link to="/mens-recovery" className="house-link">
                Learn More
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="who-we-serve-section" data-testid="who-we-serve-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">Who We Serve</h2>
            <div className="title-underline"></div>
          </div>
          <p className="section-intro">We serve:</p>
          <div className="serve-grid">
            {whoWeServe.map(({ icon: Icon, text }, index) => (
              <div key={index} className="serve-card" data-testid={`serve-card-${index}`}>
                <div className="serve-icon-wrapper">
                  <Icon size={22} />
                </div>
                <p className="serve-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="content-container">
          <div className="mission-content">
            <h2 className="mission-heading">Our Mission</h2>
            <p className="mission-text">
              ProSolve Global Solutions provides safe, structured housing and recovery support services designed to help individuals build stability, maintain recovery, and achieve greater independence.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-section" data-testid="founder-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">Meet Our Founder</h2>
            <div className="title-underline"></div>
          </div>
          <div className="founder-content">
            <div className="founder-portrait" data-testid="founder-portrait">
              <div className="founder-portrait-ring" aria-hidden="true"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_rebuild-together-5/artifacts/unklglyx_IMG_5616.jpeg"
                alt="Roxana Salazar, Founder & Program Director of ProSolve Global Solutions"
                className="founder-photo"
                loading="lazy"
                width="320"
                height="320"
              />
              <span className="founder-badge">Founder &amp; Program Director</span>
            </div>
            <div className="founder-body">
              <p className="founder-text">
                My name is <strong>Roxana Salazar</strong>, and I founded ProSolve Global Solutions because I lived the journey myself. Through recovery, faith, and determination, I rebuilt my life, reconnected with my children, and found a new purpose.
              </p>
              <p className="founder-text">
                My personal recovery journey showed me how vital safe housing, accountability, and a supportive community are when someone is rebuilding their life. Today, I am honored to help others build stability, maintain recovery, and create a pathway forward.
              </p>
              <blockquote className="founder-quote" data-testid="founder-quote">
                &ldquo;I know what it&apos;s like to rebuild a life and support loved ones through their journey. Today, I am honored to help others find stability, hope, and the opportunity to move forward.&rdquo;
                <cite className="founder-cite">— Roxana Salazar, Founder &amp; Program Director</cite>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose ProSolve */}
      <section className="why-choose-section" data-testid="why-choose-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose ProSolve?</h2>
            <div className="title-underline"></div>
          </div>
          <div className="why-choose-grid">
            {whyChoose.map(({ icon: Icon, label }, index) => (
              <div key={index} className="why-choose-card" data-testid={`why-choose-card-${index}`}>
                <div className="why-choose-icon-wrapper">
                  <Icon size={28} className="why-choose-icon" />
                </div>
                <h3 className="why-choose-label">{label}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="impact-home-section" data-testid="impact-section">
        <div className="content-container">
          <div className="section-header">
            <h2 className="section-title">Community Impact</h2>
            <div className="title-underline"></div>
          </div>
          <p className="section-intro">We help individuals:</p>
          <div className="impact-home-grid">
            {communityImpact.map((item, index) => (
              <div key={index} className="impact-home-item" data-testid={`impact-card-${index}`}>
                <Sparkles size={18} className="impact-home-icon" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="quick-links-section">
        <div className="content-container">
          <div className="quick-links-grid">
            <Link to="/apply" className="quick-link-card">
              <FileText size={32} className="quick-link-icon" />
              <h3 className="quick-link-title">Apply Now</h3>
              <p className="quick-link-description">Start your application for recovery housing</p>
            </Link>

            <Link to="/testimonials" className="quick-link-card">
              <Users size={32} className="quick-link-icon" />
              <h3 className="quick-link-title">Testimonials</h3>
              <p className="quick-link-description">Read stories from our community</p>
            </Link>

            <Link to="/donations" className="quick-link-card">
              <Heart size={32} className="quick-link-icon" />
              <h3 className="quick-link-title">Support Us</h3>
              <p className="quick-link-description">Help expand access to recovery housing!</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
